import {
  PROVIDER_SEND_TURN_MAX_ATTACHMENTS,
  PROVIDER_SEND_TURN_MAX_IMAGE_BYTES,
  type PreviewAnnotationPayload,
  type ScopedThreadRef,
} from "@cleo/contracts";

import {
  type ComposerImageAttachment,
  type DraftId,
  trackComposerImagePreparation,
  useComposerContentStore,
} from "~/composerDraftStore";
import { compressImageToByteLimit } from "~/lib/imageCompression";
import { previewAnnotationScreenshotFile } from "~/lib/previewAnnotation";
import { randomUUID } from "~/lib/utils";
import { convertHeicToJpeg, isHeicFile } from "./heicConversion";

// The one image door (UP8). Every path that puts an image into a composer
// draft — the composer's own paste and drop, the paperclip, a drop on a Room
// Rail row — goes through `addComposerImages`, so the image-only filter, the
// wire count cap, HEIC → JPEG conversion and the byte-limit compression live
// in exactly one place. Nothing here sends; the draft store keeps the images
// until Send freezes them into a message's boundary.

export type ComposerImageTarget = ScopedThreadRef | DraftId;

/** An image for the inline lane: a browser image type, or a HEIC photo the door converts. */
export function isComposerImageFile(file: {
  readonly name: string;
  readonly type: string;
}): boolean {
  return file.type.startsWith("image/") || isHeicFile(file);
}

/** Split a pick, paste or drop: images take the inline lane, everything else the file lane. */
export function partitionComposerFiles(files: ReadonlyArray<File>): {
  readonly images: File[];
  readonly files: File[];
} {
  const images: File[] = [];
  const others: File[] = [];
  for (const file of files) (isComposerImageFile(file) ? images : others).push(file);
  return { images, files: others };
}

export interface ComposerImagePlan {
  readonly accepted: File[];
  /** The last refusal, worded for the person; null when every file was accepted. */
  readonly rejected: string | null;
}

/**
 * The synchronous half: image-only and the count cap, in file order. A caller
 * that compresses asynchronously reserves `accepted.length` slots first so an
 * overlapping paste sees them.
 */
export function planComposerImageFiles(
  files: ReadonlyArray<File>,
  input: { readonly existingCount: number; readonly maxAttachments?: number },
): ComposerImagePlan {
  const maxAttachments = input.maxAttachments ?? PROVIDER_SEND_TURN_MAX_ATTACHMENTS;
  const accepted: File[] = [];
  let rejected: string | null = null;
  let reserved = input.existingCount;
  for (const file of files) {
    if (!isComposerImageFile(file)) {
      rejected = `Unsupported file type for '${file.name}'. Please attach image files only.`;
      continue;
    }
    if (reserved >= maxAttachments) {
      rejected = `You can attach up to ${maxAttachments} images per message.`;
      break;
    }
    accepted.push(file);
    reserved += 1;
  }
  return { accepted, rejected };
}

export interface AddComposerImagesOptions {
  /** Slots already reserved beyond the draft's images (pastes still compressing). */
  readonly reservedCount?: number;
  readonly maxAttachments?: number;
  readonly compress?: typeof compressImageToByteLimit;
  readonly convertHeic?: typeof convertHeicToJpeg;
  /** Runs synchronously with the accepted files, before any await, so the caller can reserve slots. */
  readonly onAccepted?: (accepted: ReadonlyArray<File>) => void;
}

export interface AddComposerImagesOutcome {
  readonly attached: number;
  /** The last refusal, worded for the person; null when everything attached. */
  readonly rejected: string | null;
}

/** Turn an accepted file into a draft image: HEIC converts, oversize downscales, the rest passes byte for byte. */
export async function prepareComposerImage(
  file: File,
  options: Pick<AddComposerImagesOptions, "compress" | "convertHeic"> = {},
): Promise<
  | { readonly ok: true; readonly image: ComposerImageAttachment }
  | { readonly ok: false; readonly rejected: string }
> {
  let source = file;
  if (isHeicFile(file)) {
    const converted = await (options.convertHeic ?? convertHeicToJpeg)(file);
    if (!converted.ok) return { ok: false, rejected: converted.detail };
    source = converted.file;
  }
  const compressed = await (options.compress ?? compressImageToByteLimit)(
    source,
    PROVIDER_SEND_TURN_MAX_IMAGE_BYTES,
  );
  if (!compressed.ok) {
    return {
      ok: false,
      rejected:
        compressed.reason === "unreadable"
          ? `'${file.name}' could not be read as an image.`
          : `'${file.name}' is too large to attach, even after compression.`,
    };
  }
  const attachmentFile = compressed.file;
  return {
    ok: true,
    image: {
      type: "image",
      id: randomUUID(),
      name: attachmentFile.name || "image",
      mimeType: attachmentFile.type,
      sizeBytes: attachmentFile.size,
      previewUrl: URL.createObjectURL(attachmentFile),
      file: attachmentFile,
    },
  };
}

/**
 * Attach image files to a composer draft. Validation is synchronous (the plan
 * and `onAccepted` run before the first await); conversion and compression
 * follow; the images land in the draft store's `addImages` door in one write.
 */
export function addComposerImages(
  threadRef: ComposerImageTarget,
  files: ReadonlyArray<File>,
  options: AddComposerImagesOptions = {},
): Promise<AddComposerImagesOutcome> {
  // Saving drafts before a Desk update waits for accepted images to land.
  return trackComposerImagePreparation(attachComposerImages(threadRef, files, options));
}

async function attachComposerImages(
  threadRef: ComposerImageTarget,
  files: ReadonlyArray<File>,
  options: AddComposerImagesOptions,
): Promise<AddComposerImagesOutcome> {
  if (files.length === 0) return { attached: 0, rejected: null };
  const store = useComposerContentStore.getState();
  const existingCount =
    (store.getComposerDraft(threadRef)?.images.length ?? 0) + (options.reservedCount ?? 0);
  const plan = planComposerImageFiles(files, {
    existingCount,
    ...(options.maxAttachments !== undefined ? { maxAttachments: options.maxAttachments } : {}),
  });
  options.onAccepted?.(plan.accepted);
  let rejected = plan.rejected;
  const images: ComposerImageAttachment[] = [];
  for (const file of plan.accepted) {
    const prepared = await prepareComposerImage(file, options);
    if (prepared.ok) images.push(prepared.image);
    else rejected = prepared.rejected;
  }
  if (images.length > 0) useComposerContentStore.getState().addImages(threadRef, images);
  return { attached: images.length, rejected };
}

/**
 * A preview annotation's screenshot joins the draft as an image under the
 * annotation's id once it is converted to a file. Saving drafts before a Desk
 * update waits for this, so an accepted annotation's screenshot is stored too.
 * A failed conversion leaves the structured annotation sendable on its own.
 */
export function attachPreviewAnnotationScreenshot(
  annotation: PreviewAnnotationPayload,
  addImage: (image: ComposerImageAttachment) => void,
  toFile: (
    annotation: PreviewAnnotationPayload,
  ) => Promise<File | null> = previewAnnotationScreenshotFile,
): Promise<ComposerImageAttachment | null> {
  return trackComposerImagePreparation(
    (async () => {
      let screenshotFile: File | null = null;
      try {
        screenshotFile = await toFile(annotation);
      } catch {
        return null;
      }
      if (!screenshotFile || !annotation.screenshot) return null;
      const image = {
        type: "image",
        id: annotation.id,
        name: screenshotFile.name,
        mimeType: screenshotFile.type,
        sizeBytes: screenshotFile.size,
        previewUrl: annotation.screenshot.dataUrl,
        file: screenshotFile,
      } satisfies ComposerImageAttachment;
      addImage(image);
      return image;
    })(),
  );
}
